import * as React from "react";
import { AutoComplete, Button, Checkbox, Input, notification, Select } from "antd";
import { NGLabelText } from "../../../newcomponents/text/labelText";
import {
  IS_DEPLOYMENT_ENV_TEST,
  LOCATION_SELECTION_ON_ACCOUNT_CREATE_ENABLED,
  MARKETING_URL,
} from "../../../envVariables";
import { request } from "../../../services/cpln";
import { getBillingErrorMessage } from "../../../services/utils";
import { Location } from "../../../schema/types/location";
import { inputValidations } from "../../../mobxDataModels/validations";
import { addressCountries } from "../../billing/addressData/countries";
import { usaStates } from "../../billing/addressData/usaStates";
import { User } from "../../../mobxStores/user/user";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { BillingContext } from "../../../mobxStores/billingContext/billingContext";
import { UserData } from "../../../mobxStores/userData/userData";
import { flowResult } from "mobx";

interface Props {
  providerId: string;
  awsAccountId: string;
  customerIdentifier: string;
}
export const CreateAccount: React.FC<Props> = ({ providerId, awsAccountId, customerIdentifier }) => {
  const [step, setStep] = React.useState<"info" | "address">("info");

  const [accountName, setAccountName] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [company, setCompany] = React.useState("");
  const [jobTitle, setJobTitle] = React.useState("");
  const [linkedin, setLinkedin] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [tou, setTou] = React.useState(false);

  const [country, setCountry] = React.useState("");
  const [state, setState] = React.useState("");
  const [city, setCity] = React.useState("");
  const [postalCode, setPostalCode] = React.useState("");
  const [line1, setLine1] = React.useState("");
  const [line2, setLine2] = React.useState("");

  const [isPressedNextFromInfo, setIsPressedNextFromInfo] = React.useState(false);
  const [isPressedNextFromAddress, setIsPressedNextFromAddress] = React.useState(false);

  const [isLoadingCreateAccount, setIsLoadingCreateAccount] = React.useState(false);

  const [createdAccountId, setCreatedAccountId] = React.useState("");
  const [createdAccountIsDisabled, setCreatedAccountIsDisabled] = React.useState(false);

  function getAccountObject() {
    let res: any = {
      email: User.email,
      fullName: fullName,
      accountName: accountName,
      extraInfo: {
        company: company,
        jobTitle: jobTitle,
      },
      phone: phone,
      address: {
        postalCode: postalCode,
        city: city,
        country: country,
        address1: line1,
      },
      provider: "aws",
      providerId: providerId,
      providerState: {
        awsAccountId: awsAccountId,
        customerIdentifier: customerIdentifier,
      },
    };
    if (linkedin) {
      res.extraInfo.linkedin = linkedin;
    }
    if (state) {
      res.address.state = state;
    }
    if (line2) {
      res.address.address2 = line2;
    }
    return res;
  }

  async function handleCreateAccount() {
    try {
      setIsLoadingCreateAccount(true);

      const account = getAccountObject();
      const { data: newAccount } = await request({
        method: "post",
        service: "billing-ng",
        url: `/account`,
        body: { account },
      });

      const _accountId = newAccount.id;
      setCreatedAccountId(_accountId);
      const _isDisabled = newAccount.enabled === false;
      setCreatedAccountIsDisabled(_isDisabled);

      // api for accept tou
      try {
        await request({
          method: "post",
          service: "billing-ng",
          url: `/account/${newAccount.id}/tou`,
          body: {
            tou: {
              customerName: newAccount.fullName,
              accepted: tou,
            },
          },
        });
      } catch (e) {
        const errorMessage = getBillingErrorMessage(e);
        console.error("Failed to succeed on tou", errorMessage);
        notification.warning({
          message: "Failed to accept terms of service",
          description: errorMessage,
          placement: "bottomRight",
        });
      }

      await flowResult(BillingContext.setAccount(newAccount.id));

      setIsLoadingCreateAccount(false);

      window.location.href = `/billing/account/${newAccount.id}`;
    } catch (e) {
      const errorMessage = getBillingErrorMessage(e);
      notification.warning({
        message: "Failed to create account",
        description: errorMessage,
        placement: "bottomRight",
      });
      setIsLoadingCreateAccount(false);
    }
  }

  const isAccountNameValid: true | string = React.useMemo(() => {
    if (!accountName) {
      return `Account Name is required`;
    }
    return true;
  }, [accountName]);
  const isFullNameValid: true | string = React.useMemo(() => {
    if (!fullName) {
      return `Full Name is required`;
    }
    return true;
  }, [fullName]);
  const isCompanyValid: true | string = React.useMemo(() => {
    if (!company) {
      return `Company is required`;
    }
    return true;
  }, [company]);
  const isJobTitleValid: true | string = React.useMemo(() => {
    if (!jobTitle) {
      return `Job Title is required`;
    }
    return true;
  }, [jobTitle]);
  const isPhoneValid: true | string = React.useMemo(() => {
    if (!phone) {
      return `Phone is required`;
    }
    if (phone.length < 10) {
      return `Phone needs to be at least 10 characters`;
    }
    return true;
  }, [phone]);
  const isTouValid: true | string = React.useMemo(() => {
    if (!tou) {
      return `Terms of Use needs to be accepted`;
    }
    return true;
  }, [tou]);
  const isValidInfo: true | string = React.useMemo(() => {
    if (isAccountNameValid !== true) {
      return isAccountNameValid;
    }
    if (isFullNameValid !== true) {
      return isFullNameValid;
    }
    if (isCompanyValid !== true) {
      return isCompanyValid;
    }
    if (isJobTitleValid !== true) {
      return isJobTitleValid;
    }
    if (isPhoneValid !== true) {
      return isPhoneValid;
    }
    if (isTouValid !== true) {
      return isTouValid;
    }
    return true;
  }, [isAccountNameValid, isFullNameValid, isCompanyValid, isJobTitleValid, isPhoneValid, isTouValid]);

  const isCountryValid: true | string = React.useMemo(() => {
    if (!country) {
      return `Country is required`;
    }
    return true;
  }, [country]);
  const isStateValid: true | string = React.useMemo(() => {
    if (!state && country === "United States") {
      return `State is required`;
    }
    return true;
  }, [state]);
  const isCityValid: true | string = React.useMemo(() => {
    if (!city) {
      return `City is required`;
    }
    return true;
  }, [city]);
  const isAddress1Valid: true | string = React.useMemo(() => {
    if (!line1) {
      return `Address Line is required`;
    }
    return true;
  }, [line1]);
  const isPostalCodeValid: true | string = React.useMemo(() => {
    if (!postalCode) {
      return `Postal Code is required`;
    }
    return true;
  }, [postalCode]);
  const isValidAddress: true | string = React.useMemo(() => {
    if (isCountryValid !== true) {
      return isCountryValid;
    }
    if (isStateValid !== true) {
      return isStateValid;
    }
    if (isCityValid !== true) {
      return isCityValid;
    }
    if (isAddress1Valid !== true) {
      return isAddress1Valid;
    }
    if (isPostalCodeValid !== true) {
      return isPostalCodeValid;
    }
    return true;
  }, [isCountryValid, isStateValid, isCityValid, isAddress1Valid, isPostalCodeValid]);

  return (
    <div>
      <div>
        <div>
          {step === "info" ? (
            <>
              <div className="mb-2">
                <NGLabelText>Account Name</NGLabelText>
                <Input value={accountName} onChange={(e) => setAccountName(e.target.value)} />
                <div
                  className={`${
                    !isPressedNextFromInfo || isAccountNameValid === true ? "invisible" : ""
                  } color-danger text-sm`}
                >
                  <span className="h-2">{isAccountNameValid === true ? "-" : isAccountNameValid}</span>
                </div>
              </div>
              <div className="mb-2">
                <NGLabelText>Full Name</NGLabelText>
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
                <div
                  className={`${
                    !isPressedNextFromInfo || isFullNameValid === true ? "invisible" : ""
                  } color-danger text-sm`}
                >
                  <span className="h-2">{isFullNameValid === true ? "-" : isFullNameValid}</span>
                </div>
              </div>
              <div className="mb-2">
                <NGLabelText>Company</NGLabelText>
                <Input value={company} onChange={(e) => setCompany(e.target.value)} />
                <div
                  className={`${
                    !isPressedNextFromInfo || isCompanyValid === true ? "invisible" : ""
                  } color-danger text-sm`}
                >
                  <span className="h-2">{isCompanyValid === true ? "-" : isCompanyValid}</span>
                </div>
              </div>
              <div className="mb-2">
                <NGLabelText>Job Title</NGLabelText>
                <Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
                <div
                  className={`${
                    !isPressedNextFromInfo || isJobTitleValid === true ? "invisible" : ""
                  } color-danger text-sm`}
                >
                  <span className="h-2">{isJobTitleValid === true ? "-" : isJobTitleValid}</span>
                </div>
              </div>
              <div className="mb-6">
                <NGLabelText>Linkedin Profile (Optional)</NGLabelText>
                <Input value={linkedin} onChange={(e) => setLinkedin(e.target.value)} />
              </div>
              <div className="mb-2">
                <NGLabelText>Phone Number</NGLabelText>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                <div
                  className={`${
                    !isPressedNextFromInfo || isPhoneValid === true ? "invisible" : ""
                  } color-danger text-sm`}
                >
                  <span className="h-2">{isPhoneValid === true ? "-" : isPhoneValid}</span>
                </div>
              </div>
              <div className="mb-2">
                <div className="flex items-center">
                  <Checkbox
                    id={"terms-of-use"}
                    aria-label={"I accept the Control Plane Terms of Use"}
                    checked={tou}
                    onChange={(e) => setTou(e.target.checked)}
                  />
                  <div className="ml-1">
                    <label className="cursor-pointer" htmlFor={`terms-of-use`}>
                      I accept the Control Plane{" "}
                    </label>
                    <a
                      className={`underline hover:underline focus:outline-none focus-visible:underline focus-visible:ring-2 color-link`}
                      target={"_blank"}
                      href={`${MARKETING_URL}/terms-of-use`}
                    >
                      Terms of Use
                    </a>
                  </div>
                </div>
                <div
                  className={`${!isPressedNextFromInfo || isTouValid === true ? "invisible" : ""} color-danger text-sm`}
                >
                  <span className="h-2">{isTouValid === true ? "-" : isTouValid}</span>
                </div>
              </div>
              <div className="flex justify-end">
                <Button className="basis-0 flex-grow invisible" type={`default`} disabled>
                  Placeholder
                </Button>
                <Button
                  className="basis-0 flex-grow"
                  type={`primary`}
                  style={{ minWidth: 200 }}
                  iconPosition="end"
                  icon={<ArrowRightOutlined />}
                  onClick={() => {
                    setIsPressedNextFromInfo(true);
                    if (isValidInfo !== true) {
                      return;
                    }
                    setStep("address");
                  }}
                >
                  Next (Address)
                </Button>
              </div>
            </>
          ) : null}
          {step === "address" ? (
            <>
              <div className="mb-2">
                <NGLabelText>Country</NGLabelText>
                <AutoComplete
                  style={{ width: "100%" }}
                  value={country}
                  onChange={(value) => setCountry(value)}
                  options={addressCountries.map((v) => ({ label: v, value: v }))}
                  filterOption={(inputValue, option) =>
                    option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                  }
                />
                <div
                  className={`${
                    !isPressedNextFromAddress || isCountryValid === true ? "invisible" : ""
                  } color-danger text-sm`}
                >
                  <span className="h-2">{isCountryValid === true ? "-" : isCountryValid}</span>
                </div>
              </div>
              <div className="mb-2">
                <NGLabelText>State {country === "United States" ? "" : "(Optional)"}</NGLabelText>
                {country === "United States" ? (
                  <AutoComplete
                    style={{ width: "100%" }}
                    value={state}
                    onChange={(value) => setState(value)}
                    options={usaStates.map((v) => ({ label: v, value: v }))}
                    filterOption={(inputValue, option) =>
                      option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                  />
                ) : (
                  <Input value={state} onChange={(e) => setState(e.target.value)} />
                )}
                <div
                  className={`${
                    !isPressedNextFromAddress || isStateValid === true ? "invisible" : ""
                  } color-danger text-sm`}
                >
                  <span className="h-2">{isStateValid === true ? "-" : isStateValid}</span>
                </div>
              </div>
              <div className="mb-2">
                <NGLabelText>City</NGLabelText>
                <Input value={city} onChange={(e) => setCity(e.target.value)} />
                <div
                  className={`${
                    !isPressedNextFromAddress || isCityValid === true ? "invisible" : ""
                  } color-danger text-sm`}
                >
                  <span className="h-2">{isCityValid === true ? "-" : isCityValid}</span>
                </div>
              </div>
              <div className="mb-2">
                <NGLabelText>Postal Code</NGLabelText>
                <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
                <div
                  className={`${
                    !isPressedNextFromAddress || isPostalCodeValid === true ? "invisible" : ""
                  } color-danger text-sm`}
                >
                  <span className="h-2">{isPostalCodeValid === true ? "-" : isPostalCodeValid}</span>
                </div>
              </div>
              <div className="mb-2">
                <NGLabelText>Address Line 1</NGLabelText>
                <Input value={line1} onChange={(e) => setLine1(e.target.value)} />
                <div
                  className={`${
                    !isPressedNextFromAddress || isAddress1Valid === true ? "invisible" : ""
                  } color-danger text-sm`}
                >
                  <span className="h-2">{isAddress1Valid === true ? "-" : isAddress1Valid}</span>
                </div>
              </div>
              <div className="mb-10">
                <NGLabelText>Address Line 2</NGLabelText>
                <Input value={line2} onChange={(e) => setLine2(e.target.value)} />
              </div>
              <div className="flex gap-4">
                <Button
                  className="basis-0 flex-grow"
                  type={`default`}
                  iconPosition={`start`}
                  icon={<ArrowLeftOutlined />}
                  disabled={isLoadingCreateAccount}
                  onClick={() => setStep("info")}
                >
                  Back (Info)
                </Button>
                <Button
                  className="basis-0 flex-grow"
                  type={`primary`}
                  iconPosition="end"
                  icon={<ArrowRightOutlined />}
                  disabled={isLoadingCreateAccount}
                  loading={isLoadingCreateAccount}
                  onClick={() => {
                    setIsPressedNextFromAddress(true);
                    if (isValidAddress !== true) {
                      return;
                    }
                    handleCreateAccount();
                  }}
                >
                  Create Billing Account
                </Button>
              </div>
            </>
          ) : null}
          {createdAccountId ? (
            <div className="mt-12 flex justify-center">
              <div>Account created, please wait while we are setting up your dashboard.</div>
            </div>
          ) : null}
          {IS_DEPLOYMENT_ENV_TEST && createdAccountId ? (
            <div className="mt-12">
              <div>ONLY VISIBLE ON TEST</div>
              <div>
                Created Account <span className="font-semibold">{accountName}</span>
              </div>
              <div>
                ID <span className="font-semibold">{createdAccountId}</span>
              </div>
              {createdAccountIsDisabled ? <div>Account is disabled</div> : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
